'use client'

import { CloseIcon } from '@repo/ui/CloseIcon'
import { Pill } from '@repo/ui/Pill'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useEffect, useState } from 'react'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'

import { THIRD_PARTY_FORMS_STATUS_ITEMS, type ThirdPartyFormsStatusValue } from './thirdPartyFormsStatuses'

export const ThirdPartyFormsStatusFilter = () => {
  const {
    thirdPartyFormsTableFilters,
    setThirdPartyFormsTableFilters,
    thirdPartyFormsTableFilterCount
  } = useDataGridFilterStore()

  const filters = thirdPartyFormsTableFilters?.filters ?? {}
  const [selectedStatuses, setSelectedStatuses] = useState<ThirdPartyFormsStatusValue[]>([])

  useEffect(() => {
    const stored = filters?.status as ThirdPartyFormsStatusValue[] | null
    if (stored && Array.isArray(stored)) {
      setSelectedStatuses(stored)
    } else {
      setSelectedStatuses([])
    }
  }, [filters?.status])

  useEffect(() => {
    if (thirdPartyFormsTableFilterCount === 0) {
      setSelectedStatuses([])
    }
  }, [thirdPartyFormsTableFilterCount])

  const handleChange = (statusValue: ThirdPartyFormsStatusValue) => {
    const isSelected = selectedStatuses.includes(statusValue)
    const next = isSelected
      ? selectedStatuses.filter((s) => s !== statusValue)
      : [...selectedStatuses, statusValue]

    setSelectedStatuses(next)
    setThirdPartyFormsTableFilters({ status: next.length > 0 ? next : null })
  }

  const handleClear = () => {
    setSelectedStatuses([])
    setThirdPartyFormsTableFilters({ status: null })
  }

  const renderValue = () => {
    if (selectedStatuses.length === 0) return 'Select'
    const first = THIRD_PARTY_FORMS_STATUS_ITEMS.find((i) => i.value === selectedStatuses[0])
    const firstLabel = first?.label ?? selectedStatuses[0]
    if (selectedStatuses.length === 1) return firstLabel
    return `${firstLabel} + ${selectedStatuses.length - 1}`
  }

  return (
    <MUIBox sx={{ width: '100%' }}>
      <MUIFormControl
        sx={{
          width: '100%',
          [`& > label`]: {
            backgroundColor: 'transparent',
            top: '-10px',
            transform: 'translate(8px, 15px) scale(1)'
          },
          [`& > label[data-shrink='true']`]: {
            backgroundColor: '#f5f6f8',
            top: 0,
            transform: 'translate(14px, -9px) scale(0.75)'
          }
        }}
      >
        <MUIInputLabel id="third-party-forms-status-filter-label">Select</MUIInputLabel>
        <MUISelect
          data-testid="third-party-forms-status-filter"
          multiple
          value={selectedStatuses}
          renderValue={renderValue}
          sx={{
            mb: 0,
            width: '100%',
            [`& .MuiSelect-select`]: {
              height: '18px !important',
              pt: '6px',
              pb: '6px',
              pl: '8px'
            },
            [`& .MuiSelect-nativeInput`]: {
              height: '32px !important',
              pt: 0,
              pb: 0,
              pl: '8px'
            },
            [`& .MuiSvgIcon-root`]: {
              right: 0
            }
          }}
        >
          {THIRD_PARTY_FORMS_STATUS_ITEMS.map((item) => (
            <MUIMenuItem
              key={item.value}
              value={item.value}
              onClick={() => handleChange(item.value)}
              sx={{
                backgroundColor: selectedStatuses.includes(item.value) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                margin: '6px 6px',
                minHeight: '38px',
                minWidth: 'calc(264px - 38px)',
                pb: 0,
                pl: '8px',
                pt: 0,
                width: '100%'
              }}
            >
              <Pill
                label={item.label}
                color={item.color}
                backgroundColor={item.backgroundColor}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            </MUIMenuItem>
          ))}
        </MUISelect>
        <MUIButton
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '24px',
            top: '12px',
            height: '0.6rem',
            width: '0.6rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <CloseIcon
            sx={{
              fill: 'black',
              height: '0.6rem',
              width: '0.6rem'
            }}
          />
        </MUIButton>
      </MUIFormControl>
    </MUIBox>
  )
}
